// The openbook corpus as a tune SOURCE (unification Phase 3): one shared way
// to list and load the 151 melody-carrying tunes, used by the ⌘K palette,
// tune-URL resolution and the Score studio's Examples dropdown. The manifest
// (id/file/title/composer) is fetched once per session and merged with the
// seven bundled favourites — bundled ids win for their files so persisted
// references and e2e selectors stay stable; bundled tunes load instantly from
// their inlined source, the rest fetch on demand.

import { OPENBOOK_TUNES } from './openbookTunes';
import { parseOpenbook } from '@/music/openbook/parse';
import type { Score } from '@/music/score/types';

export interface OpenbookLibraryEntry {
  id: string;
  file: string;
  title: string;
  composer?: string;
  /** Carries sung words. Written by scripts/build-openbook-manifest.ts only
   * when true, so absent means "no lyrics" — the browser badges rows without
   * fetching and parsing 151 sources. */
  lyrics?: boolean;
}

let manifestPromise: Promise<OpenbookLibraryEntry[]> | null = null;

/**
 * Every openbook tune, sorted by title. Resolves to just the bundled seven
 * when the manifest can't be fetched (offline dev, tests without a server).
 */
export function openbookLibrary(): Promise<OpenbookLibraryEntry[]> {
  manifestPromise ??= (async () => {
    let manifest: OpenbookLibraryEntry[] = [];
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}openbook/manifest.json`);
      if (res.ok) {
        const list: unknown = await res.json();
        if (Array.isArray(list)) manifest = list as OpenbookLibraryEntry[];
      }
    } catch {
      /* bundled-only fallback below */
    }
    const byFile = new Map(manifest.map((e) => [e.file, e]));
    const entries: OpenbookLibraryEntry[] = OPENBOOK_TUNES.map((t) => ({
      id: t.id, // the bundled id is the stable one for these files
      file: t.file,
      title: byFile.get(t.file)?.title ?? t.label,
      composer: byFile.get(t.file)?.composer,
      // The bundled seven keep their stable id but take the manifest's facts.
      lyrics: byFile.get(t.file)?.lyrics,
    }));
    const bundledFiles = new Set(OPENBOOK_TUNES.map((t) => t.file));
    for (const e of manifest) if (!bundledFiles.has(e.file)) entries.push(e);
    return entries.sort((a, b) => a.title.localeCompare(b.title));
  })();
  return manifestPromise;
}

/** Load one openbook tune as a Score (inlined source for the bundled seven). */
export async function loadOpenbookScore(entry: OpenbookLibraryEntry): Promise<Score> {
  const bundled = OPENBOOK_TUNES.find((t) => t.file === entry.file);
  const src = bundled
    ? bundled.src
    : await fetch(`${import.meta.env.BASE_URL}openbook/${entry.file}`).then((r) => {
        if (!r.ok) throw new Error(`openbook fetch failed: ${entry.file}`);
        return r.text();
      });
  // Stamp the manifest id so the tune has a STABLE identity: band settings
  // are remembered per tune under Score.id (#176), and a parsed score would
  // otherwise arrive anonymous and fall back to title-keying.
  return { ...parseOpenbook(src).score, id: `openbook-${entry.id}` };
}

/** Does this Score carry any written notes (vs a chord-only chart)? */
export function scoreHasMelody(score: Score): boolean {
  return score.parts.some((p) =>
    p.measures.some((m) =>
      m.voices.some((v) =>
        v.items.some(
          (it) =>
            (it.kind === 'note' && it.notes.length > 0) ||
            (it.kind === 'tuplet' && it.items.some((e) => e.kind === 'note' && e.notes.length > 0)),
        ),
      ),
    ),
  );
}
